import React, { useState, useEffect, useCallback } from 'react';
import { searchRecall, openRecallExport, createRecall, listRecalls, getRecall, updateRecallStatus, addRecallComment, getRecallRecipients, sendRecallEmails, previewRecallEmail, getRecallReplyTo, addRecallResponse, deleteRecallResponse, openRecallPdf, openRecallPerCustomerZip, openRecallSummaryPdf, getProducts, getRecallStandaloneBatches, getRecallScorecard, notifyFsaiForRecall, listLinesMissingBatchCode, patchLineBatchCode } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Search, Download, AlertTriangle, FileText, FileSpreadsheet, Loader2, ShieldAlert, Clock,
  MessageSquare, ArrowLeft, Send, Eye, Archive, Mail, Paperclip, History, Building2, Wrench, CheckCircle2,
  ExternalLink,
} from 'lucide-react';

const RECALL_CLASSES = {
  '1': { label: 'Class 1', desc: 'Serious adverse health consequences or death', color: 'bg-red-100 text-red-800' },
  '2': { label: 'Class 2', desc: 'Remote probability of adverse health consequences', color: 'bg-amber-100 text-amber-800' },
  '3': { label: 'Class 3', desc: 'No adverse health consequences (labelling/quality)', color: 'bg-blue-100 text-blue-800' },
};

// Operator-mandated default body for MOCK recall drills.
// Placeholders ({customer_name}, {company_name}, {reply_to} etc.) are substituted per-recipient.
const DEFAULT_MOCK_BODY = `Dear {customer_name},

Please can you confirm receipt of the products on the attached schedule.

Please reply to {reply_to}.

No further action required.

Thank you for your assistance in the MOCK recall systems test.

— {company_name}`;

const DEFAULT_LIVE_BODY = `Dear {customer_name},

URGENT — LIVE PRODUCT RECALL.

Reason: {recall_reason}
Batch code(s): {batch_codes}
Use-by date(s): {use_by_range}

The affected product schedule is attached. Please QUARANTINE all affected stock immediately and confirm receipt of this notice to {reply_to}.

A representative will be in touch within 24 hours to arrange recovery / collection.

Reference: {recall_ref}
— {company_name}`;

const RESPONSE_EVENT_LABELS = {
  notified: 'Notified',
  reply_received: 'Reply received',
  batch_confirmed: 'Batch confirmed in stock',
  batch_not_found: 'Batch not found in stock',
  expiry_dispute: 'Use-by date dispute',
  quarantined: 'Quarantined',
  returning: 'Returning to us',
  collection_req: 'Collection requested',
  disposed: 'Disposed locally',
  other: 'Other',
};

const RESPONSE_EVENT_COLORS = {
  notified: 'bg-blue-100 text-blue-800',
  reply_received: 'bg-purple-100 text-purple-800',
  batch_confirmed: 'bg-emerald-100 text-emerald-800',
  batch_not_found: 'bg-zinc-100 text-zinc-800',
  expiry_dispute: 'bg-amber-100 text-amber-800',
  quarantined: 'bg-orange-100 text-orange-800',
  returning: 'bg-indigo-100 text-indigo-800',
  collection_req: 'bg-violet-100 text-violet-800',
  disposed: 'bg-zinc-100 text-zinc-800',
  other: 'bg-slate-100 text-slate-800',
};

const STATUS_COLORS = {
  initiated: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  notified: 'bg-purple-100 text-purple-800',
  closed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-zinc-100 text-zinc-600',
};

export default function RecallSearch() {
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  // Search params
  const [batchCode, setBatchCode] = useState('');
  const [productSku, setProductSku] = useState('');
  const [productId, setProductId] = useState(''); // optional exact product scope
  const [productList, setProductList] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [fuzzySearch, setFuzzySearch] = useState(false);

  // Save as recall dialog
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [recallType, setRecallType] = useState('mock');
  const [recallClass, setRecallClass] = useState('1');
  const [recallReason, setRecallReason] = useState('');
  const [recallNotes, setRecallNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Recall history
  const [recalls, setRecalls] = useState([]);
  const [recallsLoading, setRecallsLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [recallPage, setRecallPage] = useState(1);
  const [recallTotal, setRecallTotal] = useState(0);

  // Recall detail view
  const [viewRecall, setViewRecall] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  // Notification dialog
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [resolvedRecipients, setResolvedRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState({});
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [perCustomerPdf, setPerCustomerPdf] = useState(false);
  const [extraMessage, setExtraMessage] = useState('');
  // ── Editable email preview ──
  const [emailMode, setEmailMode] = useState('mock'); // 'mock' or 'live' — drives default body
  const [subjectOverride, setSubjectOverride] = useState('');
  const [bodyOverride, setBodyOverride] = useState('');
  const [emailPreviews, setEmailPreviews] = useState([]); // per-recipient resolved previews
  const [previewLoading, setPreviewLoading] = useState(false);
  // ── Reply-to email (Feb 2026 fix) ──
  // Operator-confirmable reply-to address that replaces the hardcoded
  // qa@cashelblue.com in the default templates. Prefilled from
  // settings.recall_reply_to_email; operator can override per-recall and
  // optionally save as new default. Substitutes into `{reply_to}`
  // placeholders and sets the outgoing message's Reply-To header.
  const [replyToEmail, setReplyToEmail] = useState('');
  const [saveReplyToDefault, setSaveReplyToDefault] = useState(false);
  // ── Response Log tab ──
  const [respCustomer, setRespCustomer] = useState('');
  const [respEvent, setRespEvent] = useState('reply_received');
  const [respNotes, setRespNotes] = useState('');

  // ── P1: Recall hardening UI state ──
  // Scorecard: response/recovery metrics computed live from the response log.
  const [scorecard, setScorecard] = useState(null);
  // In-stock / on-hold / consumed batches that share the recall's batch codes
  // but were never dispatched — must be quarantined alongside customer stock.
  const [standaloneBatches, setStandaloneBatches] = useState(null);
  // FSAI notify dialog
  const [fsaiOpen, setFsaiOpen] = useState(false);
  const [fsaiTest, setFsaiTest] = useState('');
  const [fsaiExtra, setFsaiExtra] = useState('');
  const [fsaiSending, setFsaiSending] = useState(false);
  // Track elapsed time from operator opening the page until "Save as Recall"
  // — surfaces a trace-time KPI per FSAI/BRC drill expectations.
  const [searchOpenedAt, setSearchOpenedAt] = useState(null);

  // Backfill tool — review / patch dispatch lines with missing batch codes
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillRows, setBackfillRows] = useState([]);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillTotal, setBackfillTotal] = useState(0);

  useEffect(() => {
    // Capture the moment the operator lands on the Search tab so we can
    // record how long it took to assemble the recall — used as the
    // time_to_trace KPI when they click Save as Recall.
    if (activeTab === 'search' && !searchOpenedAt) {
      setSearchOpenedAt(Date.now());
    }
  }, [activeTab, searchOpenedAt]);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {};
      if (batchCode) params.batch_code = batchCode;
      if (productId) params.product_id = productId;
      if (productSku) params.product_sku = productSku;
      if (customerName) params.customer_name = customerName;
      if (poNumber) params.po_number = poNumber;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (fuzzySearch) params.fuzzy_search = true;
      const { data } = await searchRecall(params);
      const list = data.results || data || [];
      setResults(list);
      // Cross-product collision warning — when the user searched by batch
      // code alone and got hits across multiple products, flag it so they
      // realise they may need to scope by Product before proceeding.
      if (batchCode && !productId && !productSku) {
        const distinctProducts = [...new Set(list.map(r => (r.product_name || '').trim()).filter(Boolean))];
        if (distinctProducts.length > 1) {
          toast.warning(
            `Batch "${batchCode}" appears across ${distinctProducts.length} different products. Pick a Product to scope the trace.`,
            { duration: 9000 }
          );
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'pdf') => {
    if (!searched || results.length === 0) return;
    try {
      const params = {};
      if (batchCode) params.batch_code = batchCode;
      if (productId) params.product_id = productId;
      if (productSku) params.product_sku = productSku;
      if (customerName) params.customer_name = customerName;
      if (poNumber) params.po_number = poNumber;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (fuzzySearch) params.fuzzy_search = true;
      // PDF opens inline; CSV triggers a download (server sets Content-Disposition).
      await openRecallExport(params, { format });
    } catch (err) {
      toast.error(err?.message || 'Export failed');
    }
  };

  const handleSaveAsRecall = async () => {
    if (!recallReason || recallReason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setSaving(true);
    try {
      const timeToTraceSeconds = searchOpenedAt
        ? Math.max(0, (Date.now() - searchOpenedAt) / 1000)
        : null;
      const { data } = await createRecall({
        recall_type: recallType,
        recall_class: recallClass,
        reason: recallReason,
        product_description: productSku || batchCode || '',
        batch_codes: results.map(r => r.batch_code).filter(Boolean),
        use_by_range: dateFrom && dateTo ? `${dateFrom} - ${dateTo}` : '',
        search_criteria: { batchCode, productSku, customerName, poNumber, dateFrom, dateTo, productId },
        affected_records: results,
        notes: recallNotes,
        time_to_trace_seconds: timeToTraceSeconds,
      });
      toast.success(`${recallType === 'mock' ? 'Mock' : 'Live'} Recall ${data.recall_ref} created`);
      setSaveDialogOpen(false);
      setRecallReason('');
      setRecallNotes('');
      setActiveTab('history');
      loadRecalls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recall');
    } finally {
      setSaving(false);
    }
  };

  const loadRecalls = useCallback(async () => {
    setRecallsLoading(true);
    try {
      const params = { page: recallPage, page_size: 15 };
      if (filterType !== 'all') params.recall_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await listRecalls(params);
      setRecalls(data.recalls || []);
      setRecallTotal(data.total || 0);
    } catch {
      toast.error('Failed to load recall history');
    } finally {
      setRecallsLoading(false);
    }
  }, [recallPage, filterType, filterStatus]);

  useEffect(() => {
    if (activeTab === 'history') loadRecalls();
  }, [activeTab, loadRecalls]);

  // Load active products once for the Product scope dropdown.
  // The dropdown is the recommended way to scope a recall trace and prevents
  // cross-product batch-code collisions (e.g. Cashel Blue accidentally
  // pulled into a Crozier Blue recall because they share a batch code).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await getProducts({ active_only: true });
        if (!mounted) return;
        const list = Array.isArray(data) ? data : (data.products || data.items || []);
        setProductList(list);
      } catch {
        // Silent — search still works without the dropdown
      }
    })();
    return () => { mounted = false; };
  }, []);

  const openRecallDetail = async (recallId) => {
    setViewLoading(true);
    try {
      const { data } = await getRecall(recallId);
      setViewRecall(data);
      // Load scorecard + standalone batches in parallel — both
      // augment the recall view with FSAI/BRC compliance data.
      const [score, standalone] = await Promise.allSettled([
        getRecallScorecard(recallId),
        getRecallStandaloneBatches(recallId),
      ]);
      setScorecard(score.status === 'fulfilled' ? score.value.data : null);
      setStandaloneBatches(standalone.status === 'fulfilled' ? standalone.value.data : null);
    } catch {
      toast.error('Failed to load recall');
    } finally {
      setViewLoading(false);
    }
  };

  const refreshScorecard = async () => {
    if (!viewRecall) return;
    try {
      const { data } = await getRecallScorecard(viewRecall.id);
      setScorecard(data);
    } catch { /* non-fatal */ }
  };

  const handleNotifyFsai = async () => {
    if (!viewRecall) return;
    setFsaiSending(true);
    try {
      const payload = {
        include_pdf: true,
        extra_message: fsaiExtra || undefined,
        test_recipient: fsaiTest ? fsaiTest.trim() : undefined,
      };
      const { data } = await notifyFsaiForRecall(viewRecall.id, payload);
      toast.success(`FSAI notification sent to ${data.to}`);
      setFsaiOpen(false);
      setFsaiTest('');
      setFsaiExtra('');
      openRecallDetail(viewRecall.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'FSAI notification failed');
    } finally {
      setFsaiSending(false);
    }
  };

  const openBackfill = async () => {
    setBackfillOpen(true);
    setBackfillLoading(true);
    try {
      const { data } = await listLinesMissingBatchCode({ page_size: 200 });
      setBackfillRows(data.rows || []);
      setBackfillTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load missing batch codes');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handlePatchLine = async (row, batchCodeValue, useByDateValue) => {
    if (!batchCodeValue || batchCodeValue.length < 1) {
      toast.error('Batch code is required');
      return;
    }
    try {
      await patchLineBatchCode({
        dispatch_id: row.dispatch_id,
        line_index: row.line_index,
        batch_code: batchCodeValue.trim(),
        use_by_date: useByDateValue || undefined,
        reason: 'Recall traceability backfill',
      });
      toast.success(`Patched ${row.dispatch_number} line ${row.line_index + 1}`);
      // Remove from list locally instead of full reload
      setBackfillRows((prev) => prev.filter((r) => !(r.dispatch_id === row.dispatch_id && r.line_index === row.line_index)));
      setBackfillTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Patch failed');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !viewRecall) return;
    try {
      await addRecallComment(viewRecall.id, { comment: newComment.trim() });
      setNewComment('');
      openRecallDetail(viewRecall.id);
      toast.success('Comment added');
    } catch {
      toast.error('Failed to add comment');
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || !viewRecall) return;
    try {
      await updateRecallStatus(viewRecall.id, { status: newStatus, notes: statusNotes });
      setNewStatus('');
      setStatusNotes('');
      openRecallDetail(viewRecall.id);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDownloadPdf = async () => {
    if (!viewRecall) return;
    await openRecallPdf(viewRecall.id);
  };

  const handleDownloadCustomerSidePdf = async () => {
    if (!viewRecall) return;
    await openRecallPdf(viewRecall.id, { customerSide: true });
  };

  const openNotifyDialog = async () => {
    if (!viewRecall) return;
    setNotifyOpen(true);
    setNotifyLoading(true);
    setTestMode(false);
    setTestEmail('');
    setExtraMessage('');
    setIncludePdf(true);
    // Default per-customer PDF ON for LIVE recalls (each customer should only see their own data),
    // OFF for mock drills (operator usually wants a single audit PDF copy).
    setPerCustomerPdf(viewRecall.recall_type === 'live');
    // Seed the editable subject + body from the recall_type. Operator can edit
    // either before clicking Send. Mock template is the operator-mandated wording.
    const isMock = viewRecall.recall_type === 'mock';
    setEmailMode(isMock ? 'mock' : 'live');
    setSubjectOverride(
      isMock
        ? `[MOCK RECALL DRILL] Please confirm receipt — ${viewRecall.recall_ref || ''}`
        : `[URGENT RECALL] ${viewRecall.recall_ref || ''} — please action immediately`
    );
    setBodyOverride(isMock ? DEFAULT_MOCK_BODY : DEFAULT_LIVE_BODY);
    setEmailPreviews([]);
    // Fetch the tenant's saved reply-to email so the field prefills the
    // operator's confirmed inbox instead of the historic hardcoded
    // qa@cashelblue.com. Operator can override per-recall.
    setSaveReplyToDefault(false);
    try {
      const { data: rt } = await getRecallReplyTo();
      setReplyToEmail(rt.reply_to_email || '');
    } catch {
      setReplyToEmail('');
    }
    try {
      const { data } = await getRecallRecipients(viewRecall.id);
      const recips = data.resolved_recipients || [];
      setResolvedRecipients(recips);
      // Pre-select every resolved recipient
      const sel = {};
      recips.forEach((r) => { sel[r.email] = true; });
      setSelectedEmails(sel);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load recipients');
      setResolvedRecipients([]);
      setSelectedEmails({});
    } finally {
      setNotifyLoading(false);
    }
  };

  // When operator flips MOCK <-> LIVE, swap the body in-place ONLY if they
  // haven't already started customising it. Detect customisation by comparing
  // against the current "default" for the OTHER mode.
  const handleSwitchEmailMode = (mode) => {
    setEmailMode(mode);
    const currentlyOnDefault = bodyOverride === DEFAULT_MOCK_BODY || bodyOverride === DEFAULT_LIVE_BODY || !bodyOverride;
    if (currentlyOnDefault) {
      setBodyOverride(mode === 'mock' ? DEFAULT_MOCK_BODY : DEFAULT_LIVE_BODY);
      setSubjectOverride(
        mode === 'mock'
          ? `[MOCK RECALL DRILL] Please confirm receipt — ${viewRecall?.recall_ref || ''}`
          : `[URGENT RECALL] ${viewRecall?.recall_ref || ''} — please action immediately`
      );
    } else {
      toast.info('Custom subject/body kept — clear the box to load the default for this mode.');
    }
  };

  const handleAddResponse = async () => {
    if (!viewRecall || !respCustomer || !respEvent) return;
    try {
      await addRecallResponse(viewRecall.id, {
        customer_name: respCustomer,
        event_type: respEvent,
        notes: respNotes,
      });
      toast.success('Response logged');
      setRespNotes('');
      // Refresh the recall
      const { data } = await getRecall(viewRecall.id);
      setViewRecall(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log response');
    }
  };

  const handleDeleteResponse = async (entryId) => {
    if (!viewRecall) return;
    if (!window.confirm('Remove this response entry? This action is audit-logged.')) return;
    try {
      await deleteRecallResponse(viewRecall.id, entryId);
      const { data } = await getRecall(viewRecall.id);
      setViewRecall(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePreviewEmail = async () => {
    if (!viewRecall) return;
    setPreviewLoading(true);
    setEmailPreviews([]);
    try {
      // Send only the SELECTED recipients to the preview endpoint
      const recipients = resolvedRecipients
        .filter((r) => selectedEmails[r.email])
        .map((r) => ({ email: r.email, customer_name: r.customer_name }));
      const { data } = await previewRecallEmail(viewRecall.id, {
        recipients,
        subject_override: subjectOverride || undefined,
        body_override: bodyOverride || undefined,
        extra_message: extraMessage || undefined,
        reply_to_email: (replyToEmail || '').trim() || undefined,
      });
      setEmailPreviews(data.previews || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleEmail = (email) => {
    setSelectedEmails((prev) => ({ ...prev, [email]: !prev[email] }));
  };

  const handleSendNotifications = async () => {
    if (!viewRecall) return;
    // Reply-to must be a valid email — this is the address customers
    // will reply to, and it's substituted into the email body via
    // `{reply_to}`. Refuse to send if the operator cleared it or
    // typed something malformed.
    const cleanedReplyTo = (replyToEmail || '').trim();
    if (!cleanedReplyTo || !/^\S+@\S+\.\S+$/.test(cleanedReplyTo)) {
      toast.error('Please enter a valid Reply-to email before sending');
      return;
    }
    // Safety guard: block sending MOCK-worded emails on a LIVE recall.
    // The backend also strips these lines defensively, but catching this
    // in the UI prevents the operator from being surprised.
    if (viewRecall.recall_type === 'live' && bodyOverride) {
      const lowered = bodyOverride.toLowerCase();
      if (lowered.includes('mock recall') || lowered.includes('systems test') || lowered.includes('no further action required')) {
        if (!window.confirm(
          'This is a LIVE recall but the body still contains MOCK / drill wording ' +
          '("MOCK recall", "systems test", or "No further action required"). ' +
          'These lines will be stripped automatically before sending. Continue?'
        )) return;
      }
    }
    let payload = {
      include_pdf: includePdf,
      per_customer_pdf: includePdf && perCustomerPdf,
      extra_message: extraMessage,
      subject_override: subjectOverride || undefined,
      body_override: bodyOverride || undefined,
      reply_to_email: cleanedReplyTo,
      save_reply_to_default: !!saveReplyToDefault,
    };
    if (testMode) {
      if (!testEmail || !/^\S+@\S+\.\S+$/.test(testEmail)) {
        toast.error('Enter a valid test email');
        return;
      }
      payload.test_recipient = testEmail.trim();
    } else {
      const picked = resolvedRecipients
        .filter((r) => selectedEmails[r.email])
        .map((r) => ({ email: r.email, customer_name: r.customer_name }));
      if (picked.length === 0) {
        toast.error('Select at least one recipient');
        return;
      }
      payload.recipients = picked;
    }

    if (viewRecall.recall_type === 'live' && !testMode) {
      if (!window.confirm(`This will send a LIVE RECALL email to ${payload.recipients.length} recipient(s). Continue?`)) return;
    }

    setSendingEmails(true);
    try {
      const { data } = await sendRecallEmails(viewRecall.id, payload);
      if (data.sent > 0 && data.failed === 0) {
        toast.success(`${data.sent} recall email(s) sent successfully`);
      } else if (data.sent > 0) {
        toast.warning(`${data.sent} sent | ${data.failed} failed. See timeline for details.`);
      } else {
        const err = data.failures?.[0]?.error || 'No emails sent';
        toast.error(`Send failed: ${err}`);
      }
      setNotifyOpen(false);
      openRecallDetail(viewRecall.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingEmails(false);
    }
  };

  // ── Recall Detail View ──
  if (viewRecall) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setViewRecall(null)} data-testid="btn-back-recalls">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              {viewRecall.recall_ref}
              <Badge className={viewRecall.recall_type === 'live' ? 'bg-red-100 text-red-800 ml-2' : 'bg-blue-100 text-blue-800 ml-2'}>
                {viewRecall.recall_type === 'live' ? 'LIVE RECALL' : 'MOCK RECALL'}
              </Badge>
              <Badge className={STATUS_COLORS[viewRecall.status] || 'bg-zinc-100'}>
                {viewRecall.status?.replace('_', ' ')}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Created {new Date(viewRecall.created_at).toLocaleDateString('en-IE')} by {viewRecall.initiated_by}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="default"
              size="sm"
              onClick={() => openRecallSummaryPdf(viewRecall.id)}
              data-testid="btn-recall-summary-pdf"
              title="Full audit-ready recall summary PDF — every event, log, dispatch, batch, customer contact and compliance-log entry in one paginated document."
            >
              <FileText className="w-4 h-4 mr-1.5" /> Full Recall Summary PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="btn-recall-pdf">
              <FileText className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCustomerSidePdf}
              data-testid="btn-recall-pdf-customer-side"
              title="Customer-Side Copy — same content, customer names masked so it can be shared between affected customers without disclosing identities."
            >
              <FileText className="w-4 h-4 mr-1.5" /> Customer-Side PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openRecallPerCustomerZip(viewRecall.id)}
              data-testid="btn-recall-per-customer-zip"
              title="Download a ZIP containing one PDF per affected customer — each PDF shows only that customer's affected records (no masking, no cross-customer disclosure)."
            >
              <FileText className="w-4 h-4 mr-1.5" /> Per-Customer ZIP
            </Button>
            <Button
              variant={viewRecall.recall_type === 'live' ? 'destructive' : 'default'}
              size="sm"
              onClick={openNotifyDialog}
              data-testid="btn-recall-notify"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              {viewRecall.recall_type === 'live' ? 'Send LIVE Recall Notices' : 'Send Mock Drill Emails'}
            </Button>
            {viewRecall.recall_type === 'live' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFsaiOpen(true)}
                data-testid="btn-notify-fsai"
                className="border-red-300 text-red-700 hover:bg-red-50"
                title="Send the pre-filled FSAI notification (incidents@fsai.ie). Required per FSAI Guidance Note 10 for LIVE Class 1/2 recalls."
              >
                <Building2 className="w-4 h-4 mr-1.5" /> Notify FSAI
              </Button>
            )}
          </div>
        </div>

        {/* ── Scorecard: response & recovery KPIs (FSAI / BRC drill evidence) ── */}
        {scorecard && (
          <Card data-testid="recall-scorecard">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Recall Scorecard
                {scorecard.time_to_trace_seconds != null && (
                  <Badge variant="outline" className="ml-2 text-xs font-mono">
                    Trace time: {scorecard.time_to_trace_seconds < 60
                      ? `${scorecard.time_to_trace_seconds.toFixed(1)}s`
                      : `${(scorecard.time_to_trace_seconds / 60).toFixed(1)} min`}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground uppercase">Notified</div>
                  <div className="text-xl font-bold">{scorecard.customers_notified}<span className="text-sm text-muted-foreground"> / {scorecard.customers_total}</span></div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground uppercase">Response Rate</div>
                  <div className="text-xl font-bold">{scorecard.response_rate_pct}%</div>
                  <div className="text-xs text-muted-foreground">{scorecard.customers_responded} replied</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground uppercase">Recovery (kg)</div>
                  <div className="text-xl font-bold">{scorecard.recovery_rate_pct}%</div>
                  <div className="text-xs text-muted-foreground">{scorecard.recovered_kg} / {scorecard.total_at_risk_kg} kg</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground uppercase">Quarantined</div>
                  <div className="text-xl font-bold">{scorecard.batches_quarantined}</div>
                  <div className="text-xs text-muted-foreground">batches</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground uppercase">Avg Reply</div>
                  <div className="text-xl font-bold">{scorecard.avg_response_hours != null ? `${scorecard.avg_response_hours}h` : '—'}</div>
                </div>
              </div>
              {(scorecard.actions || []).length > 0 && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Recommended actions
                  </div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    {scorecard.actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground uppercase">Recall Class</div>
              <Badge className={RECALL_CLASSES[viewRecall.recall_class]?.color || ''}>
                {RECALL_CLASSES[viewRecall.recall_class]?.label}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">{RECALL_CLASSES[viewRecall.recall_class]?.desc}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground uppercase">Affected</div>
              <div className="text-2xl font-bold">{viewRecall.total_records} records</div>
              <p className="text-xs text-muted-foreground">{viewRecall.customers_affected?.length || 0} customers | {viewRecall.total_quantity} units</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground uppercase">Batch Codes</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {viewRecall.batch_codes?.slice(0, 5).map((b, i) => (
                  <Badge key={i} variant="outline" className="font-mono text-xs">{b}</Badge>
                ))}
                {(viewRecall.batch_codes?.length || 0) > 5 && <Badge variant="secondary">+{viewRecall.batch_codes.length - 5} more</Badge>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Reason for Recall</CardTitle></CardHeader>
          <CardContent><p>{viewRecall.reason}</p>{viewRecall.notes && <p className="text-sm text-muted-foreground mt-2">{viewRecall.notes}</p>}</CardContent>
        </Card>

        {/* Affected Records Table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Affected Dispatches ({viewRecall.affected_records?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Cust Code</TableHead>
                    <TableHead>Product</TableHead><TableHead>Batch</TableHead><TableHead>Use By</TableHead>
                    <TableHead>Qty</TableHead><TableHead>PO</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(viewRecall.affected_records || []).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm">
                        {r.dispatch_id ? (
                          <a
                            href={`/dispatch/${r.dispatch_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:underline inline-flex items-center gap-1"
                            data-testid={`recall-affected-order-${r.dispatch_number}`}
                            title="Open dispatch in a new tab"
                          >
                            {r.dispatch_number}
                            <ExternalLink className="w-3 h-3 opacity-60" />
                          </a>
                        ) : (
                          r.dispatch_number
                        )}
                      </TableCell>
                      <TableCell>{r.customer_name}</TableCell>
                      <TableCell className="font-mono text-blue-700 text-sm">{r.customer_product_code || '-'}</TableCell>
                      <TableCell>{r.product_name}</TableCell>
                      <TableCell className="font-mono font-medium">{r.batch_code}</TableCell>
                      <TableCell>{r.use_by_date}</TableCell>
                      <TableCell className="font-mono">{r.quantity}</TableCell>
                      <TableCell className="font-mono text-sm">{r.po_number}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* ── Standalone batches (in-stock / on-hold / consumed but never dispatched) ──
             FSAI requires quarantine of all matching-batch product, including
             stock that never reached a customer. */}
        {standaloneBatches && (standaloneBatches.total > 0) && (
          <Card data-testid="standalone-batches-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                Standalone Batches Requiring Quarantine ({standaloneBatches.total})
              </CardTitle>
              <CardDescription className="text-xs">
                Batches sharing the recall's batch codes that have NOT been dispatched. These must be physically quarantined and their disposition recorded.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {[
                ['in_stock', 'In Stock — Available', 'bg-amber-50 border-amber-200'],
                ['on_hold', 'QC On-Hold', 'bg-orange-50 border-orange-200'],
                ['consumed', 'Consumed (not dispatched)', 'bg-zinc-50 border-zinc-200'],
              ].map(([key, label, cls]) => {
                const rows = standaloneBatches[key] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={key} className={`rounded-md border ${cls} p-3 mb-3`}>
                    <div className="text-xs font-semibold uppercase mb-2">{label} ({rows.length})</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground">
                            <th className="px-2 py-1">Batch</th>
                            <th className="px-2 py-1">Product</th>
                            <th className="px-2 py-1">Available</th>
                            <th className="px-2 py-1">Production</th>
                            <th className="px-2 py-1">Use By</th>
                            <th className="px-2 py-1">QC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((b) => (
                            <tr key={b.batch_id} className="border-t border-white/60">
                              <td className="px-2 py-1 font-mono">{b.batch_code}</td>
                              <td className="px-2 py-1">{b.product_name}</td>
                              <td className="px-2 py-1 font-mono">{b.quantity_available} / {b.quantity_produced}</td>
                              <td className="px-2 py-1">{b.production_date}</td>
                              <td className="px-2 py-1">{b.use_by_date || '—'}</td>
                              <td className="px-2 py-1">
                                <Badge variant="outline" className="text-[10px]">{b.qc_status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Status Timeline */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Status Timeline</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(viewRecall.status_history || []).map((h, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 shrink-0" />
                    <div>
                      <span className="font-medium">{h.status?.replace('_', ' ')}</span>
                      <span className="text-muted-foreground"> &mdash; {h.user}</span>
                      <p className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString('en-IE')}</p>
                      {h.notes && <p className="text-xs mt-0.5">{h.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {viewRecall.status !== 'closed' && viewRecall.status !== 'cancelled' && (
                <div className="mt-4 pt-3 border-t space-y-2">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Update status..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="notified">Notified</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  {newStatus && (
                    <>
                      <Input placeholder="Notes (optional)" value={statusNotes} onChange={e => setStatusNotes(e.target.value)} className="h-8 text-sm" />
                      <Button size="sm" onClick={handleStatusUpdate}>Update Status</Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Comments</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[200px] overflow-y-auto">
                {(viewRecall.comments || []).length === 0 && <p className="text-sm text-muted-foreground">No comments yet</p>}
                {(viewRecall.comments || []).map((c, i) => (
                  <div key={i} className="bg-muted/30 rounded p-2.5 text-sm">
                    <p>{c.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{c.user} &mdash; {new Date(c.timestamp).toLocaleString('en-IE')}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Input placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} className="h-8 text-sm" data-testid="input-recall-comment" />
                <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()}>
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Response Log (FSAI / BRC drill evidence) ──
            Time-stamped log of every customer interaction. The "notified"
            entries are auto-logged when emails are dispatched; everything
            else is logged manually as customers reply. */}
        <Card data-testid="card-recall-response-log">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4" /> Response Log
              <Badge variant="secondary" className="ml-1">{(viewRecall.customer_responses || []).length} entries</Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              Time-stamped audit trail of customer notifications, replies, and actions taken (batch confirmed, quarantined, returning, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Entry form */}
            <div className="grid gap-2 md:grid-cols-12 mb-3 pb-3 border-b">
              <div className="md:col-span-3">
                <Label className="text-xs">Customer</Label>
                <Select value={respCustomer} onValueChange={setRespCustomer}>
                  <SelectTrigger className="h-9" data-testid="select-response-customer">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {(viewRecall.customers_affected || []).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Event</Label>
                <Select value={respEvent} onValueChange={setRespEvent}>
                  <SelectTrigger className="h-9" data-testid="select-response-event">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RESPONSE_EVENT_LABELS).filter(([k]) => k !== 'notified').map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-5">
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  value={respNotes}
                  onChange={(e) => setRespNotes(e.target.value)}
                  placeholder="e.g. QA Manager Tom confirmed batch B-1 in chiller A"
                  className="h-9"
                  data-testid="input-response-notes"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button
                  size="sm"
                  onClick={handleAddResponse}
                  disabled={!respCustomer || !respEvent}
                  data-testid="btn-add-response"
                  className="h-9 w-full"
                  title="Log this response entry"
                >
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Entries — newest first */}
            <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="response-log-list">
              {(viewRecall.customer_responses || []).length === 0 && (
                <p className="text-sm text-muted-foreground">No response entries yet. Auto-logged "notified" entries will appear here when emails are dispatched.</p>
              )}
              {[...(viewRecall.customer_responses || [])]
                .sort((a, b) => (b.occurred_at || '').localeCompare(a.occurred_at || ''))
                .map((e) => (
                  <div
                    key={e.id}
                    className="flex items-start gap-3 p-2.5 border rounded bg-zinc-50/40"
                    data-testid={`response-entry-${e.id}`}
                  >
                    <Badge className={`text-[10px] ${RESPONSE_EVENT_COLORS[e.event_type] || 'bg-zinc-100'}`}>
                      {RESPONSE_EVENT_LABELS[e.event_type] || e.event_type}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{e.customer_name}</p>
                      {e.notes && <p className="text-sm text-zinc-700 mt-0.5">{e.notes}</p>}
                      {e.contact_email && (
                        <p className="text-[11px] font-mono text-zinc-500 mt-0.5">{e.contact_email}</p>
                      )}
                      <p className="text-[11px] text-zinc-500 mt-1">
                        {new Date(e.occurred_at || e.logged_at).toLocaleString('en-IE')}
                        {' · '}
                        {e.logged_by}
                        {e.system_generated && ' · auto'}
                      </p>
                    </div>
                    {!e.system_generated && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 h-7"
                        onClick={() => handleDeleteResponse(e.id)}
                        title="Remove entry (audit-logged)"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Email Notification History */}
        {(viewRecall.email_history?.length || 0) > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4" /> Notification History
                <Badge variant="secondary" className="ml-1">{viewRecall.email_history.length} sent</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[260px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead className="w-12">PDF</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...viewRecall.email_history].reverse().map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>
                          <Badge className={h.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                            {h.success ? 'sent' : 'failed'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{h.email}</TableCell>
                        <TableCell className="text-sm">{h.customer_name || '-'}</TableCell>
                        <TableCell className="text-xs truncate max-w-xs">{h.subject}</TableCell>
                        <TableCell>{h.attachment_included ? <Paperclip className="w-3.5 h-3.5 text-emerald-600" /> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString('en-IE')}</TableCell>
                        <TableCell className="text-xs text-red-700">{h.error ? (h.error.length > 80 ? h.error.slice(0, 80) + '…' : h.error) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Notify Dialog ── */}
        <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
          <DialogContent
            className="sm:max-w-[620px] max-h-[92vh] p-0 flex flex-col gap-0 overflow-hidden"
            data-testid="notify-recall-dialog"
          >
            <DialogHeader className="px-6 pt-6 pb-4 border-b bg-background shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-red-600" />
                {viewRecall.recall_type === 'live' ? 'Send LIVE Recall Notifications' : 'Send Mock Recall Drill Emails'}
              </DialogTitle>
              <DialogDescription>
                {viewRecall.recall_type === 'mock'
                  ? 'Mock drill emails are clearly labelled "MOCK RECALL DRILL — NO ACTION REQUIRED" to satisfy FSAI/BRC annual drill evidence requirements.'
                  : 'LIVE recall emails are sent with URGENT priority. This is a formal regulatory action — double check recipients before sending.'}
              </DialogDescription>
            </DialogHeader>

            {notifyLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Checkbox checked={testMode} onCheckedChange={(v) => setTestMode(!!v)} id="cb-test-mode" />
                  <label htmlFor="cb-test-mode" className="cursor-pointer">Preview mode — send to a single test address only</label>
                </div>

                {testMode ? (
                  <div>
                    <Label className="text-xs font-medium">Test Recipient Email</Label>
                    <Input
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="qa.team@cashelblue.ie"
                      className="mt-1"
                      data-testid="input-test-recipient"
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs font-medium">
                      Resolved Customer Recipients ({resolvedRecipients.length})
                    </Label>
                    <div className="mt-1 border rounded max-h-[180px] overflow-y-auto">
                      {resolvedRecipients.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground text-center">
                          No email addresses found for affected customers.
                          Use "Preview mode" above, or add email addresses on the Customers page.
                        </div>
                      ) : (
                        resolvedRecipients.map((r) => (
                          <label
                            key={r.email}
                            className="flex items-center gap-2 p-2 border-b last:border-0 hover:bg-muted/30 text-sm cursor-pointer"
                          >
                            <Checkbox
                              checked={!!selectedEmails[r.email]}
                              onCheckedChange={() => toggleEmail(r.email)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium flex items-center gap-2">
                                <span className="truncate">{r.customer_name}</span>
                                {r.source === 'incident' && (
                                  <Badge variant="destructive" className="text-[9px] h-4 px-1.5">Incident</Badge>
                                )}
                                {r.source === 'primary' && (
                                  <Badge variant="secondary" className="text-[9px] h-4 px-1.5">Primary</Badge>
                                )}
                                {r.source === 'extra' && (
                                  <Badge variant="outline" className="text-[9px] h-4 px-1.5">Extra</Badge>
                                )}
                              </div>
                              <div className="font-mono text-xs text-muted-foreground truncate">{r.email}</div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Checkbox checked={includePdf} onCheckedChange={(v) => setIncludePdf(!!v)} id="cb-include-pdf" />
                    <label htmlFor="cb-include-pdf" className="cursor-pointer">Attach FSAI/BRC PDF report</label>
                  </div>
                  <div className="flex items-start gap-2 text-sm pl-6">
                    <Checkbox
                      checked={perCustomerPdf}
                      onCheckedChange={(v) => setPerCustomerPdf(!!v)}
                      id="cb-per-customer-pdf"
                      disabled={!includePdf || testMode}
                      data-testid="cb-per-customer-pdf"
                    />
                    <label htmlFor="cb-per-customer-pdf" className={`cursor-pointer ${!includePdf || testMode ? 'opacity-50' : ''}`}>
                      Send each customer their own filtered PDF
                      <span className="block text-xs text-muted-foreground">
                        Each recipient receives a PDF containing only the records dispatched to their company — no other customers' data. Recommended for LIVE recalls.
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium">Additional Message (optional)</Label>
                  <Textarea
                    rows={3}
                    value={extraMessage}
                    onChange={(e) => setExtraMessage(e.target.value)}
                    placeholder="Additional context or instructions for recipients…"
                    className="mt-1"
                  />
                </div>

                {/* ── Reply-to email (replaces the historic hardcoded qa@cashelblue.com) ── */}
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="reply-to-block">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-900">Reply-to email</span>
                    <span className="text-[10px] text-blue-700">
                      Recipients will reply here (also substituted into <code>{`{reply_to}`}</code> in the body)
                    </span>
                  </div>
                  <Input
                    type="email"
                    value={replyToEmail}
                    onChange={(e) => setReplyToEmail(e.target.value)}
                    placeholder="e.g. louiscb@cashelblue.com"
                    className="h-9 font-mono text-xs"
                    data-testid="input-reply-to-email"
                  />
                  <label className="flex items-center gap-2 text-[11px] text-blue-900 cursor-pointer">
                    <Checkbox
                      checked={saveReplyToDefault}
                      onCheckedChange={(v) => setSaveReplyToDefault(!!v)}
                      id="cb-save-reply-to"
                      data-testid="cb-save-reply-to"
                    />
                    <span>Save this address as the default for future recall notifications</span>
                  </label>
                </div>

                {/* ── LIVE-recall mismatch warning ── */}
                {viewRecall?.recall_type === 'live' && emailMode === 'mock' && (
                  <div
                    className="rounded-lg border-2 border-red-400 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="live-mock-mismatch-warning"
                  >
                    <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      <strong>Wording mismatch:</strong> this recall is <strong>LIVE</strong> but the email body is set to <strong>MOCK</strong>.
                      Switch to &ldquo;Actual Recall / Withdrawal&rdquo; below, or the MOCK-specific lines
                      (&ldquo;MOCK recall systems test&rdquo;, &ldquo;No further action required&rdquo;) will be stripped before sending.
                    </span>
                  </div>
                )}

                {/* ── MOCK / ACTUAL toggle ── drives the default email body ── */}
                <div className="rounded-lg border bg-zinc-50 p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Email content</p>
                  <div className="flex items-center gap-4 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="cb-email-mock">
                      <input
                        type="checkbox"
                        checked={emailMode === 'mock'}
                        onChange={() => handleSwitchEmailMode('mock')}
                      />
                      <span>MOCK recall (drill — please confirm receipt)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="cb-email-live">
                      <input
                        type="checkbox"
                        checked={emailMode === 'live'}
                        onChange={() => handleSwitchEmailMode('live')}
                      />
                      <span className="text-red-700 font-medium">Actual Recall / Withdrawal</span>
                    </label>
                  </div>
                  <div>
                    <Label className="text-xs">Subject (placeholders: {`{customer_name}, {recall_ref}, {batch_codes}, {company_name}, {reply_to}`})</Label>
                    <Input
                      value={subjectOverride}
                      onChange={(e) => setSubjectOverride(e.target.value)}
                      className="h-9 mt-1 font-mono text-xs"
                      data-testid="input-email-subject-override"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Body</Label>
                    <Textarea
                      rows={9}
                      value={bodyOverride}
                      onChange={(e) => setBodyOverride(e.target.value)}
                      className="mt-1 font-mono text-xs"
                      data-testid="input-email-body-override"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Placeholders are substituted per-recipient. Use the Preview button to see exactly what each customer will receive.</p>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviewEmail}
                      disabled={previewLoading || notifyLoading}
                      data-testid="btn-preview-email"
                    >
                      {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                      Preview per recipient
                    </Button>
                    {emailPreviews.length > 0 && (
                      <span className="text-xs text-muted-foreground">{emailPreviews.length} previews ready below</span>
                    )}
                  </div>
                  {emailPreviews.length > 0 && (
                    <div className="border rounded bg-white p-2 max-h-[260px] overflow-y-auto space-y-3" data-testid="email-previews-pane">
                      {emailPreviews.map((p, i) => (
                        <div key={i} className="text-xs border-b last:border-0 pb-2 last:pb-0">
                          <p className="font-medium flex items-center gap-2">
                            <Mail className="w-3 h-3" />
                            {p.customer_name}
                            <span className="font-mono text-[10px] text-zinc-500">&lt;{p.email}&gt;</span>
                          </p>
                          <p className="mt-1"><span className="text-zinc-500">Subject:</span> {p.subject}</p>
                          <pre className="mt-1 whitespace-pre-wrap font-sans text-[11px] bg-zinc-50 p-1.5 rounded">{p.body}</pre>
                          <p className="mt-1 text-[10px] text-zinc-500">
                            Customer-side qty: {p.total_qty_kg} kg · batches: {p.batch_codes}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {viewRecall.recall_type === 'live' && !testMode && (
                  <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>This sends a <strong>LIVE recall email</strong> to selected customers. You must also notify the FSAI within 4 hours of identifying the hazard.</span>
                  </div>
                )}
                </div>
              </div>
            )}

            <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
              <Button variant="outline" onClick={() => setNotifyOpen(false)} disabled={sendingEmails}>Cancel</Button>
              <Button
                variant={viewRecall.recall_type === 'live' ? 'destructive' : 'default'}
                onClick={handleSendNotifications}
                disabled={sendingEmails || notifyLoading}
                data-testid="btn-confirm-send-recall-emails"
              >
                {sendingEmails ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {testMode ? 'Send Test' : viewRecall.recall_type === 'live' ? 'Send LIVE Notices' : 'Send Mock Emails'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── FSAI Notification Dialog ── */}
        <Dialog open={fsaiOpen} onOpenChange={setFsaiOpen}>
          <DialogContent
            className="sm:max-w-[560px] max-h-[92vh] p-0 flex flex-col gap-0 overflow-hidden"
            data-testid="fsai-notify-dialog"
          >
            <DialogHeader className="px-6 pt-6 pb-4 border-b bg-background shrink-0">
              <DialogTitle className="text-red-700 flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Notify FSAI
              </DialogTitle>
              <DialogDescription>
                Sends the pre-filled LIVE recall notification email (incidents@fsai.ie). Per FSAI Guidance Note 10, Class 1/2 LIVE recalls must be reported to the competent authority without delay. The full recall PDF will be attached.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
              <div>
                <Label className="text-xs">Test recipient (optional)</Label>
                <Input
                  value={fsaiTest}
                  onChange={(e) => setFsaiTest(e.target.value)}
                  placeholder="e.g. louiscb@cashelblue.com — sends to this address INSTEAD of FSAI"
                  data-testid="fsai-test-recipient"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to send to FSAI (incidents@fsai.ie). For drill / preview, enter an internal address.
                </p>
              </div>
              <div>
                <Label className="text-xs">Additional context for FSAI (optional)</Label>
                <Textarea
                  rows={4}
                  value={fsaiExtra}
                  onChange={(e) => setFsaiExtra(e.target.value)}
                  placeholder="e.g. corrective action initiated, supplier notified, customer batches recovered to date..."
                  data-testid="fsai-extra-message"
                />
              </div>
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
              <Button variant="outline" onClick={() => setFsaiOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleNotifyFsai}
                disabled={fsaiSending}
                data-testid="btn-confirm-notify-fsai"
              >
                {fsaiSending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {fsaiTest ? 'Send TEST to address above' : 'Send to FSAI'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Main View (Tabs: Search / History) ──
  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-600" />
            Recall Trace Enquiry
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            FSAI & BRC compliant recall management — search, trace, and manage product recalls
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={openBackfill}
          data-testid="btn-open-backfill"
          title="Review and fill in missing batch codes on historic dispatch lines so future traces are complete."
        >
          <Wrench className="w-4 h-4 mr-1.5" /> Backfill Missing Batch Codes
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="search" data-testid="tab-recall-search">
            <Search className="w-4 h-4 mr-1.5" /> Search & Trace
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-recall-history">
            <Archive className="w-4 h-4 mr-1.5" /> Recall History
          </TabsTrigger>
        </TabsList>

        {/* ── Search Tab ── */}
        <TabsContent value="search">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium">Batch Code</Label>
                  <Input value={batchCode} onChange={e => setBatchCode(e.target.value)} placeholder="e.g. CB-240420A" className="mt-1" data-testid="recall-batch-code" />
                </div>
                <div>
                  <Label className="text-xs font-medium flex items-center gap-1">
                    Product
                    <span className="text-[10px] text-muted-foreground font-normal">(scope — prevents batch-code collisions)</span>
                  </Label>
                  <Select value={productId || 'all'} onValueChange={v => setProductId(v === 'all' ? '' : v)}>
                    <SelectTrigger className="mt-1" data-testid="recall-product-id">
                      <SelectValue placeholder="All products" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All products (no scope)</SelectItem>
                      {productList.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}{p.sku ? ` — ${p.sku}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Product SKU</Label>
                  <Input value={productSku} onChange={e => setProductSku(e.target.value)} placeholder="optional — partial match" className="mt-1" data-testid="recall-product-sku" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Customer Name</Label>
                  <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="e.g. Gourmet Foods" className="mt-1" data-testid="recall-customer" />
                </div>
                <div>
                  <Label className="text-xs font-medium">PO Number</Label>
                  <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-GF-2026" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Use-By Date From</Label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Use-By Date To</Label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1" />
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={fuzzySearch} onCheckedChange={setFuzzySearch} />
                  Fuzzy matching (partial batch codes)
                </label>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={!searched || results.length === 0}
                        data-testid="btn-recall-export"
                      >
                        <Download className="w-4 h-4 mr-2" /> Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="btn-recall-export-pdf">
                        <FileText className="w-4 h-4 mr-2" /> Export as PDF (FSAI trace)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="btn-recall-export-csv">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {searched && results.length > 0 && (
                    <Button variant="destructive" onClick={() => setSaveDialogOpen(true)} data-testid="btn-save-as-recall">
                      <ShieldAlert className="w-4 h-4 mr-2" /> Save as Recall
                    </Button>
                  )}
                  <Button onClick={handleSearch} disabled={loading} data-testid="btn-recall-search">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {searched && (
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {results.length > 0 ? `${results.length} affected dispatch(es) found` : 'No results found'}
                </CardTitle>
              </CardHeader>
              {results.length > 0 && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Order #</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Date</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Customer</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Cust Code</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Product</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Batch</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Use By</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">Qty</th>
                          <th className="text-left px-3 py-2 text-xs font-medium uppercase text-muted-foreground">PO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((r, i) => (
                          <tr key={i} className="border-b hover:bg-muted/20">
                            <td className="px-3 py-2 font-mono">
                              {r.dispatch_id ? (
                                <a
                                  href={`/dispatch/${r.dispatch_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-700 hover:underline inline-flex items-center gap-1"
                                  data-testid={`recall-result-order-${r.dispatch_number}`}
                                  title="Open dispatch in a new tab"
                                >
                                  {r.dispatch_number}
                                  <ExternalLink className="w-3 h-3 opacity-60" />
                                </a>
                              ) : (
                                r.dispatch_number
                              )}
                            </td>
                            <td className="px-3 py-2">{r.dispatch_date}</td>
                            <td className="px-3 py-2 font-medium">{r.customer_name}</td>
                            <td className="px-3 py-2 font-mono text-blue-700">{r.customer_product_code || '-'}</td>
                            <td className="px-3 py-2">{r.product_name}</td>
                            <td className="px-3 py-2 font-mono font-medium">{r.batch_code}</td>
                            <td className="px-3 py-2">{r.use_by_date}</td>
                            <td className="px-3 py-2 font-mono">{r.quantity}</td>
                            <td className="px-3 py-2 font-mono text-sm">{r.po_number}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </TabsContent>

        {/* ── Recall History Tab ── */}
        <TabsContent value="history">
          <div className="flex items-center gap-3 mb-4">
            <Select value={filterType} onValueChange={v => { setFilterType(v); setRecallPage(1); }}>
              <SelectTrigger className="w-[140px] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="mock">Mock Only</SelectItem>
                <SelectItem value="live">Live Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); setRecallPage(1); }}>
              <SelectTrigger className="w-[160px] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="notified">Notified</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground">{recallTotal} recall(s)</div>
          </div>

          {recallsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : recalls.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No recalls found. Use the Search tab to trace products and save results as a recall.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {recalls.map(r => (
                <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openRecallDetail(r.id)} data-testid={`recall-${r.id}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ShieldAlert className={`w-5 h-5 shrink-0 ${r.recall_type === 'live' ? 'text-red-600' : 'text-blue-500'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-sm">{r.recall_ref}</span>
                            <Badge className={r.recall_type === 'live' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'} variant="secondary">
                              {r.recall_type}
                            </Badge>
                            <Badge className={RECALL_CLASSES[r.recall_class]?.color || ''} variant="secondary">
                              {RECALL_CLASSES[r.recall_class]?.label}
                            </Badge>
                            <Badge className={STATUS_COLORS[r.status] || 'bg-zinc-100'} variant="secondary">
                              {r.status?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.reason?.substring(0, 80)}{r.reason?.length > 80 ? '...' : ''}</p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div>{r.total_records} records | {r.customers_affected?.length || 0} customers</div>
                        <div>{new Date(r.created_at).toLocaleDateString('en-IE')}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {recallTotal > 15 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button variant="outline" size="sm" disabled={recallPage <= 1} onClick={() => setRecallPage(p => p - 1)}>Previous</Button>
                  <span className="text-sm text-muted-foreground py-1.5">Page {recallPage}</span>
                  <Button variant="outline" size="sm" disabled={recallPage * 15 >= recallTotal} onClick={() => setRecallPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Save as Recall Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="save-recall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              Save as Recall
            </DialogTitle>
            <DialogDescription>
              Record this trace as a formal recall for FSAI/BRC compliance. {results.length} affected record(s) will be attached.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Recall Type</Label>
                <Select value={recallType} onValueChange={setRecallType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mock">Mock Recall (drill)</SelectItem>
                    <SelectItem value="live">LIVE Recall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">FSAI Recall Class</Label>
                <Select value={recallClass} onValueChange={setRecallClass}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Class 1 — Serious health risk</SelectItem>
                    <SelectItem value="2">Class 2 — Remote health risk</SelectItem>
                    <SelectItem value="3">Class 3 — No health risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {recallType === 'live' && (
              <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This will create a <strong>LIVE recall record</strong>. This is a formal regulatory action. Ensure you are authorised to initiate this recall.</span>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium">Reason for Recall *</Label>
              <Textarea
                value={recallReason}
                onChange={e => setRecallReason(e.target.value)}
                placeholder="Describe the reason for this recall (e.g. 'Potential Listeria contamination detected in batch CB-240420A during routine testing')..."
                rows={3}
                className="mt-1"
                data-testid="input-recall-reason"
              />
              <p className={`text-[11px] mt-1 ${recallReason.length === 0 ? 'text-muted-foreground' : recallReason.length < 5 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {recallReason.length === 0
                  ? 'Required — minimum 5 characters.'
                  : recallReason.length < 5
                    ? `Add ${5 - recallReason.length} more character(s) to enable Create.`
                    : `${recallReason.length} characters — ready to create.`}
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">Additional Notes</Label>
              <Textarea value={recallNotes} onChange={e => setRecallNotes(e.target.value)} placeholder="Internal notes, reference numbers..." rows={2} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button variant={recallType === 'live' ? 'destructive' : 'default'} onClick={handleSaveAsRecall} disabled={saving || recallReason.length < 5} data-testid="btn-confirm-recall">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              {recallType === 'live' ? 'Create LIVE Recall' : 'Create Mock Recall'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Backfill Missing Batch Codes Tool ── */}
      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-teal-700" /> Backfill Missing Batch Codes
            </DialogTitle>
            <DialogDescription>
              These historic dispatch lines were saved without a batch code, so they cannot be returned in a recall trace. Review each line and enter the correct production batch code. All edits are audit-logged with the operator name + timestamp.
            </DialogDescription>
          </DialogHeader>
          {backfillLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : backfillRows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              No dispatch lines with missing batch codes. Your traceability data is complete.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">{backfillTotal} line(s) remaining</div>
              {backfillRows.map((row) => (
                <BackfillRow key={`${row.dispatch_id}-${row.line_index}`} row={row} onPatch={handlePatchLine} />
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfillOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Single-row editor for the Backfill dialog. State kept local so each row
// has its own controlled batch_code / use_by inputs without re-rendering
// the whole table on every keystroke.
function BackfillRow({ row, onPatch }) {
  const [bc, setBc] = useState('');
  const [useBy, setUseBy] = useState(row.use_by_date || '');
  return (
    <div className="rounded-md border p-3 bg-zinc-50/50">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Dispatch</div>
          <div className="font-mono">{row.dispatch_number}</div>
          <div className="text-xs text-muted-foreground">{row.dispatch_date}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Customer</div>
          <div>{row.customer_name}</div>
          <div className="text-xs text-muted-foreground">{row.po_number || '—'}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Product</div>
          <div>{row.product_name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Qty</div>
          <div className="font-mono">{row.quantity}</div>
          <div className="text-xs text-muted-foreground">{row.weight_kg?.toFixed(2)} kg</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Batch code *</div>
          <Input value={bc} onChange={(e) => setBc(e.target.value)} placeholder="e.g. 25420502" data-testid={`backfill-bc-${row.dispatch_id}-${row.line_index}`} />
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Use-By (optional)</div>
          <Input value={useBy} onChange={(e) => setUseBy(e.target.value)} placeholder="DD/MM/YYYY" />
        </div>
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={!bc} onClick={() => onPatch(row, bc, useBy)} data-testid={`btn-backfill-apply-${row.dispatch_id}-${row.line_index}`}>
          Apply
        </Button>
      </div>
    </div>
  );
}
